import {
  Avatar,
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Switch,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Sparkles, Wallet } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import useThemeStore from "../services/store/useThemeStore";
import NavItem from "./SidebarNavItem";

import { ChatBubble } from "@material-ui/icons";
import {
  AccountCircle as AccountCircleIcon,
  Book as BookIcon,
  Coffee as CoffeeIcon,
  Collections,
  ExpandLess,
  ExpandMore,
  Help as HelpIcon,
  Home as HomeIcon,
  ViewKanban as KanbanIcon,
  LiveTv as LiveTvIcon,
  MusicNote as MusicNoteIcon,
  PhotoCamera as PhotoCameraIcon,
  PostAdd,
  Event as PremiumContentIcon,
  Settings as SettingsIcon,
  Verified as VerifiedIcon,
  VideoLibrary,
  ViewList,
} from "@mui/icons-material";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import { TimeIcon } from "@mui/x-date-pickers";
import { Image as ImageIcon } from "lucide-react";
import { GiftIcon } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { ZIM } from "zego-zim-web";
import { AIRBANK_ROUTES } from "../components/airbank/constants";
import { ZEGO_APP_ID } from "../constants/config";
import {
  AIRBANK_DARK_BORDER_SUBTLE,
  AIRBANK_DARK_CANVAS,
  AIRBANK_LIGHT_CANVAS,
} from "../constants/airbankSurfaces";
import { useCurrentTalentProfile } from "../hooks";
import { useGetAITwinConversations } from "../services/api/ai-twins/aiTwinConversations";
import { useGetMyAit } from "../services/api/airbank/airbank.api";
import { useMessageDataStore } from "../services/store/UseMessageData";
import { playNotificationSound } from "../utils/notificationSound";
import SocketHandler from "../utils/socketHandler";
import { getMessageToken } from "../utils/zegoTokenManager";
import { alpha } from "@mui/material/styles";

const drawerWidth = 240;

type TalentSidebarProps = {
  open: boolean;
  handleClose?: () => void;
};

const TalentSidebar = ({ open, handleClose }: TalentSidebarProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { darkMode: isDarkMode } = useThemeStore();
  const [suitOpen, setSuitOpen] = useState(false);
  const [fanCardOpen, setFanCardOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [recentMessageSender, setRecentMessageSender] = useState<string>("");

  const [zim, setZim] = useState<any>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // const [conversations, setConversations] = useState<any[]>([]);
  const [zegoToken, setZegoToken] = useState<string | null>(null);
  const { unreadMessageCounter, setUnreadMessageCounter } =
    useMessageDataStore();

  const { currentProfile } = useCurrentTalentProfile();
  const currrentPath = location.pathname;
  const messagePage = currrentPath === "/messages";
  const talentId = currentProfile?.id ? Number(currentProfile.id) : undefined;
  const numericTalentId = talentId ?? 0;

  // Use same source as AirbankStatusPage / AirbankRegistrationPage: useGetMyAit
  // When my-ait returns 200 with data: [], treat as not registered (no flicker)
  const { data: myAitData, isLoading: myAitLoading } = useGetMyAit({
    key: ["airbank-my-ait", talentId],
    params: { talentId: numericTalentId },
    options: {
      enabled: !!talentId,
      staleTime: 30000,
    },
  });

  // Single source of truth: 200 + empty data [] => not registered; non-empty data with ait_dna => registered
  // While loading, treat as not registered so we don't flash "Airbank" then switch to "Register for Airbank"
  const hasMyAitData =
    !myAitLoading &&
    Array.isArray(myAitData?.data) &&
    (myAitData?.data?.length ?? 0) > 0 &&
    !!myAitData?.data?.[0]?.ait_dna;
  const isAirbankRegistered = !!hasMyAitData;

  // Check current route to determine which menu item should be selected
  const isOnStatusPage = location.pathname === AIRBANK_ROUTES.STATUS;
  const isOnRegisterPage = location.pathname === AIRBANK_ROUTES.REGISTER;
  const isOnManageOrEditPage =
    location.pathname === AIRBANK_ROUTES.MANAGE ||
    location.pathname === AIRBANK_ROUTES.EDIT;

  // Handle Airbank menu item click - no refetch here to avoid flicker; cache is shared with status/register pages
  const handleAirbankClick = () => {
    if (isMobile) {
      handleClose?.();
    }
  };

  // AI Twin message notification state
  const [aitUnreadCount, setAitUnreadCount] = useState<number>(0);

  // Fetch AI Twin conversations to get all thread IDs for this talent
  const { data: aitConversationsData } = useGetAITwinConversations({
    key: ["ai-twin-sidebar-conversations", talentId],
    params: {
      talentId: talentId || 0,
      limit: 100,
      offset: 0,
    },
    options: {
      enabled: !!talentId,
      staleTime: 60000, // Cache for 1 minute
    },
  });

  // TP/TN toggle logic (mirrors BusinessSidebar)
  const isTPPage =
    currrentPath.includes("/talents") || currrentPath === "/talents";
  const isTNPage =
    currrentPath.includes("/TNpage") || currrentPath === "/TNpage";

  const getSavedTPTNPreference = (): "TP" | "TN" => {
    const saved = localStorage.getItem("TPTN_PREFERENCE");
    return saved === "TP" ? "TP" : "TN";
  };

  useEffect(() => {
    if (isTPPage) {
      localStorage.setItem("TPTN_PREFERENCE", "TP");
    } else if (isTNPage) {
      localStorage.setItem("TPTN_PREFERENCE", "TN");
    }
  }, [isTPPage, isTNPage]);

  const preferredView = isTPPage
    ? "TP"
    : isTNPage
      ? "TN"
      : getSavedTPTNPreference();
  const isTPPreferred = preferredView === "TP";

  const handleTPTNToggle = () => {
    if (isTPPage) {
      navigate("/TNpage");
    } else if (isTNPage) {
      navigate("/talents");
    } else {
      if (preferredView === "TP") {
        navigate("/talents");
      } else {
        navigate("/TNpage");
      }
    }
  };

  const handleSuitToggle = () => setSuitOpen(!suitOpen);
  const handleFanCardToggle = () => setFanCardOpen(!fanCardOpen);

  const handleBookingToggle = () => setBookingOpen(!bookingOpen);

  const userId = `talent-${currentProfile?.id}`;
  const userName = currentProfile?.username ?? "";
  const avatarUrl = currentProfile?.profile_image || "";

  useEffect(() => {
    if (zegoToken || !userId) return;

    getMessageToken(userId)
      .then((result) => {
        setZegoToken(result.token);
      })
      .catch((err) => {
        console.error("Failed to get message token:", err.message || err);
      });
  }, [currentProfile?.id, userId, zegoToken]);

  const queryConversation = async (zimInstance: any) => {
    try {
      setIsLoading(true);
      const conversationResult = await zimInstance?.queryConversationList({
        count: 20,
      });

      const unreadMessageCount = (
        conversationResult?.conversationList || []
      ).reduce(
        (acc: number, conversation: any) =>
          acc + (conversation.unreadMessageCount || 0),
        0,
      );
      setUnreadMessageCounter(unreadMessageCount);
    } catch (error) {
      console.error("Failed to query conversations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (zegoToken && !messagePage) {
      const initializeZIM = async () => {
        try {
          let zimInstance = ZIM?.getInstance();
          if (zimInstance) {
            zimInstance?.logout();
          }

          ZIM.create({ appID: Number(ZEGO_APP_ID) });
          zimInstance = ZIM.getInstance();
          // Setup event listeners
          const handleConnectionStateChanged = (zim: any, info: any) => {
            if (info.state !== 0 || info.state !== 3) {
              // 1 means connected/logged in
              setIsLoggedIn(true);
              queryConversation(zimInstance);
            } else {
              setIsLoggedIn(false);
            }
          };

          const handleError = (info: any) => {
            console.error("ZIM Error:", info);
          };

          const handlePeerMessageReceived = (zim: any, info: any) => {
            console.log("Peer message received:", info);
            if (!info?.info?.isOfflineMessage) {
              playNotificationSound();
              setRecentMessageSender(info.fromConversationID);
              enqueueSnackbar(
                `${info?.messageList?.[0]?.extendedData} : ${info?.messageList?.[0]?.message}`,
                {
                  variant: "info",
                  autoHideDuration: 3000,
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                  },
                },
              );
            }
            // Refresh conversation list when new messages arrive
            queryConversation(zimInstance);
          };

          const handleConversationChanged = (zim: any, info: any) => {
            console.log("Conversation changed, updating list:", info);
            queryConversation(zimInstance);
          };

          zimInstance?.on(
            "connectionStateChanged",
            handleConnectionStateChanged,
          );
          zimInstance?.on("error", handleError);
          zimInstance?.on("peerMessageReceived", handlePeerMessageReceived);
          zimInstance?.on("conversationChanged", handleConversationChanged);

          // Login to ZIM
          await zimInstance?.login(userId, {
            userName: userName ?? "",
            token: zegoToken ?? "",
            customStatus: "",
            isOfflineLogin: false,
          });
          zimInstance?.updateUserAvatarUrl(currentProfile?.profile_image || "");

          setZim(zimInstance);
          setIsLoggedIn(true);
          queryConversation(zimInstance);
        } catch (error) {
          console.error("Failed to initialize ZIM:", error);
        }
      };

      initializeZIM();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zegoToken, userId, messagePage]); // zim intentionally excluded to avoid recreation loop

  // Cleanup effect for event listeners
  useEffect(() => {
    return () => {
      if (zim) {
        try {
          zim.off("connectionStateChanged");
          zim.off("error");
          zim.off("peerMessageReceived");
          zim.off("conversationChanged");
          zim.logout();
        } catch (error) {
          console.warn("Failed to remove event listeners:", error);
        }
      }
    };
  }, [zim]);

  // Global WebSocket listener for AI Twin messages
  useEffect(() => {
    if (!talentId) return;

    const socket = SocketHandler.getInstance();
    const conversations = aitConversationsData?.data || [];
    const threadSubscriptions: string[] = [];

    if (conversations.length === 0) {
      // If no conversations yet, subscribe to a talent-level event pattern
      // Backend might emit: on-twin-chat-talent_${talentId} or similar
      const talentEvent = `on-twin-chat-talent_${talentId}`;

      socket.subscribeToCustomEvent(talentEvent, (data: any) => {
        // Verify this message is for this talent's AI Twin
        if (data?.talent_id === talentId || data?.talentId === talentId) {
          setAitUnreadCount((prev) => prev + 1);
        }
      });

      threadSubscriptions.push(talentEvent);
    } else {
      // Subscribe to all thread events for this talent's AI Twin
      conversations.forEach((conv: any) => {
        const threadId = conv.thread_id || conv.id;
        if (!threadId) return;

        // Normalize thread ID (remove "thread_" prefix if present)
        const normalizedId = threadId.toString().startsWith("thread_")
          ? threadId.toString().replace(/^thread_/, "")
          : threadId.toString();

        const chatEvent = `on-twin-chat-thread_${normalizedId}`;
        const interruptEvent = `on-twin-interrupt-thread_${normalizedId}`;

        // Subscribe to new messages in this thread
        socket.subscribeToCustomEvent(chatEvent, (data: any) => {
          // Verify this is for the correct talent
          if (
            data?.talent_id === talentId ||
            data?.talentId === talentId ||
            !data?.talent_id
          ) {
            setAitUnreadCount((prev) => prev + 1);
          }
        });

        threadSubscriptions.push(chatEvent, interruptEvent);
      });
    }

    return () => {
      threadSubscriptions.forEach((eventName) => {
        socket.unsubscribeFromCustomEvent(eventName);
      });
    };
  }, [talentId, aitConversationsData]);

  // Reset unread count when user navigates to AI Twin monitoring page
  useEffect(() => {
    if (
      currrentPath.includes("/talent/ai-twin/monitor") ||
      currrentPath.includes("/talent/ai-twins")
    ) {
      setAitUnreadCount(0);
    }
  }, [currrentPath]);

  // Airbank-only navigation: keep only AiT, Airbank, and AIBooth.
  // Everything else is intentionally not rendered below.
  const AIRBANK_ONLY_NAV = true;
  if (AIRBANK_ONLY_NAV) {
    return (
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        onClose={handleClose}
        sx={{
          width: open ? 252 : 76,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? 252 : 76,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            background: isDarkMode ? AIRBANK_DARK_CANVAS : AIRBANK_LIGHT_CANVAS,
            borderRight: isDarkMode
              ? `1px solid ${AIRBANK_DARK_BORDER_SUBTLE}`
              : "1px solid rgba(226, 232, 240, 0.95)",
            overflowX: "hidden",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            boxShadow: "none",
          },
        }}
      >
        <Box>
          <Toolbar
            sx={{
              minHeight: 80,
              display: "flex",
              alignItems: "center",
              justifyContent: open ? "flex-start" : "center",
              px: open ? 2.5 : 0,
              borderBottom: `1px solid ${isDarkMode ? alpha("#94a3b8", 0.14) : alpha("#0f172a", 0.08)}`,
              background: isDarkMode ? AIRBANK_DARK_CANVAS : AIRBANK_LIGHT_CANVAS,
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontFamily: "'Lexend', sans-serif",
                letterSpacing: "0.06em",
                background: "linear-gradient(90deg, #f59e0b 0%, #ea580c 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: "1.05rem",
              }}
            >
              AIRBANK
            </Typography>
          </Toolbar>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              pt: 1,
              flex: 1,
              pb: 2,
            }}
          >
            <List
              disablePadding
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                pt: 1,
                px: 0.75,
              }}
            >
              {open && (
                <Typography
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: isDarkMode ? "rgba(251, 191, 36, 0.9)" : "#b45309",
                  }}
                >
                  Airbank Studio
                </Typography>
              )}
              {/* Airbank */}
              {isAirbankRegistered ? (
                <NavItem
                  item={{
                    text: "Airbank Status",
                    icon: <VerifiedIcon />,
                    path: AIRBANK_ROUTES.STATUS,
                  }}
                  open={open}
                  selected={isOnStatusPage || isOnManageOrEditPage}
                  onClick={handleAirbankClick}
                />
              ) : (
                <NavItem
                  item={{
                    text: "Register Airbank",
                    icon: <VerifiedIcon />,
                    path: AIRBANK_ROUTES.REGISTER,
                  }}
                  open={open}
                  selected={isOnRegisterPage}
                  onClick={handleAirbankClick}
                />
              )}

              {/* AiT */}
              <NavItem
                item={{
                  text: "AI Twin",
                  icon: <AccountCircleIcon />,
                  path:
                    aitUnreadCount > 0 && talentId
                      ? `/talent/ai-twin/monitor/${talentId}`
                      : "/talent/ai-twins",
                }}
                open={open}
                selected={
                  location.pathname === "/talent/ai-twins" ||
                  location.pathname.includes("/talent/ai-twin/monitor")
                }
                onClick={() => {
                  if (aitUnreadCount > 0 && talentId) {
                    setAitUnreadCount(0);
                  }
                  if (isMobile) {
                    handleClose?.();
                  }
                }}
                badgeContent={aitUnreadCount > 0 ? aitUnreadCount : undefined}
              />

              {/* AIBooth */}
              <NavItem
                item={{
                  text: "AI Booth",
                  icon: <PhotoCameraIcon />,
                  path: "/talent/aibooth",
                }}
                open={open}
                selected={location.pathname.includes("/talent/aibooth")}
                onClick={isMobile ? handleClose : undefined}
              />
            </List>
            <Divider
              sx={{
                mt: 1.5,
                mx: open ? 1.5 : 1,
                borderColor: isDarkMode
                  ? "rgba(148, 163, 184, 0.18)"
                  : "rgba(15, 23, 42, 0.08)",
              }}
            />
          </Box>
        </Box>
      </Drawer>
    );
  }

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={open}
      onClose={handleClose}
      sx={{
        width: open ? drawerWidth : 72,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? drawerWidth : 72,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          background: isDarkMode ? AIRBANK_DARK_CANVAS : "var(--neutralWhite)",
          borderRight: isDarkMode
            ? `1px solid ${AIRBANK_DARK_BORDER_SUBTLE}`
            : "1px solid rgba(226, 232, 240, 0.8)",
          overflowX: "hidden",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          boxShadow: isDarkMode
            ? "0 8px 32px 0 rgba(0, 0, 0, 0.4), inset 0 1px 0 0 rgba(255, 255, 255, 0.05)"
            : "0 8px 32px 0 rgba(0, 0, 0, 0.08), inset 0 1px 0 0 rgba(255, 255, 255, 0.8)",

          scrollbarWidth: "thin",
          scrollbarColor: isDarkMode
            ? "var(--primary-300) transparent"
            : "var(--neutral-400) transparent",

          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: isDarkMode
              ? "rgba(148, 163, 184, 0.3)"
              : "rgba(148, 163, 184, 0.4)",
            borderRadius: "10px",
            border: "2px solid transparent",
            backgroundClip: "content-box",
            transition: "background-color 0.2s ease",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: isDarkMode
              ? "rgba(148, 163, 184, 0.5)"
              : "rgba(148, 163, 184, 0.6)",
          },
        },
      }}
    >
      <Box>
        <Toolbar
          sx={{
            minHeight: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "flex-start" : "center",
            px: open ? 2.5 : 0,
            background: isDarkMode
              ? AIRBANK_DARK_CANVAS
              : "linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(5, 91, 181, 0.02) 100%)",
            borderBottom: `1px solid ${
              isDarkMode ? alpha("#94a3b8", 0.14) : "rgba(226, 232, 240, 0.8)"
            }`,
          }}
        >
          {open && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #197FD3 0%, #005BB5 50%, #197FD3 100%)",
                  backgroundSize: "200% 200%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isDarkMode
                    ? "0 4px 12px rgba(25, 118, 210, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                    : "0 4px 12px rgba(25, 118, 210, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                  animation: "shimmer 3s ease-in-out infinite",
                  "@keyframes shimmer": {
                    "0%, 100%": {
                      backgroundPosition: "0% 50%",
                    },
                    "50%": {
                      backgroundPosition: "100% 50%",
                    },
                  },
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: "1.1rem",
                    fontFamily: "'Lexend', sans-serif",
                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  T
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontFamily: "'Lexend', sans-serif",
                  background: isDarkMode
                    ? "linear-gradient(135deg, #E4E4E7 0%, #A1A1AA 100%)"
                    : "linear-gradient(135deg, #1E293B 0%, #475569 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: "1.25rem",
                }}
              >
                TaasNet
              </Typography>
            </Box>
          )}
          {!open && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background:
                  "linear-gradient(135deg, #197FD3 0%, #005BB5 50%, #197FD3 100%)",
                backgroundSize: "200% 200%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: isDarkMode
                  ? "0 4px 12px rgba(25, 118, 210, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                  : "0 4px 12px rgba(25, 118, 210, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                animation: "shimmer 3s ease-in-out infinite",
                "@keyframes shimmer": {
                  "0%, 100%": {
                    backgroundPosition: "0% 50%",
                  },
                  "50%": {
                    backgroundPosition: "100% 50%",
                  },
                },
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: "1.1rem",
                  fontFamily: "'Lexend', sans-serif",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                }}
              >
                T
              </Typography>
            </Box>
          )}
        </Toolbar>

        <Box sx={{ display: "flex", flexDirection: "column", pt: 1 }}>
          {/* Profile */}
          <List
            disablePadding
            sx={{ display: "flex", flexDirection: "column", gap: 0.5, pt: 1 }}
          >
            <NavItem
              item={{
                text: currentProfile?.username ?? "profile",
                icon: (
                  <Avatar src={currentProfile?.profile_image ?? ""}>
                    {currentProfile?.username?.[0]}
                  </Avatar>
                ),
                path: "/talent/profile",
              }}
              open={open}
              onClick={isMobile ? handleClose : undefined}
              selected={location.pathname === "/talent/profile"}
            />
          </List>

          <Divider
            sx={{
              width: "80%",
              my: 1.5,
              mx: "auto",
              borderColor: isDarkMode
                ? "rgba(148, 163, 184, 0.15)"
                : "rgba(226, 232, 240, 0.8)",
              "&::before, &::after": {
                borderColor: isDarkMode
                  ? "rgba(148, 163, 184, 0.15)"
                  : "rgba(226, 232, 240, 0.8)",
              },
            }}
          />

          {/* TP/TN Toggle Component */}
          <Box
            sx={{
              mx: 1,
              mb: 0.5,
            }}
          >
            <ListItemButton
              onClick={handleTPTNToggle}
              sx={{
                borderRadius: 2.5,
                minHeight: 52,
                justifyContent: open ? "flex-start" : "center",
                px: open ? 2.5 : 1.5,
                py: 1.4,
                fontFamily: "'Lexend', sans-serif",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                backgroundColor:
                  isTPPage || isTNPage
                    ? isDarkMode
                      ? "rgba(25, 118, 210, 0.15)"
                      : "rgba(25, 118, 210, 0.1)"
                    : "transparent",
                color:
                  isTPPage || isTNPage
                    ? "var(--primary-700)"
                    : isDarkMode
                      ? "var(--neutral-100)"
                      : "var(--neutral-1000)",
                border:
                  isTPPage || isTNPage
                    ? isDarkMode
                      ? "1px solid rgba(25, 118, 210, 0.3)"
                      : "1px solid rgba(25, 118, 210, 0.2)"
                    : "1px solid transparent",
                boxShadow:
                  isTPPage || isTNPage
                    ? isDarkMode
                      ? "0 2px 8px rgba(25, 118, 210, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
                      : "0 2px 8px rgba(25, 118, 210, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)"
                    : "none",
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(148, 163, 184, 0.1)"
                    : "rgba(241, 245, 249, 0.8)",
                  color: isDarkMode
                    ? "var(--primary-400)"
                    : "var(--primary-700)",
                  transform: "translateY(-2px)",
                  boxShadow: isDarkMode
                    ? "0 6px 20px rgba(25, 118, 210, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
                    : "0 6px 20px rgba(25, 118, 210, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                  border: isDarkMode
                    ? "1px solid rgba(25, 118, 210, 0.4)"
                    : "1px solid rgba(25, 118, 210, 0.3)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 1.5 : 0,
                  justifyContent: "center",
                  color:
                    isTPPage || isTNPage
                      ? "var(--primary-700)"
                      : isDarkMode
                        ? "var(--neutralWhite)"
                        : "#000000",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <HomeIcon sx={{ fontSize: "1.3rem" }} />
              </ListItemIcon>
              {open ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: !isTPPreferred ? 600 : 400,
                      fontSize: "0.875rem",
                      fontFamily: "'Lexend', sans-serif",
                      color: !isTPPreferred
                        ? "var(--primary-700)"
                        : isDarkMode
                          ? "var(--neutralWhite)"
                          : "#000000",
                      transition: "all 0.2s ease",
                    }}
                  >
                    TN
                  </Typography>
                  <Switch
                    checked={isTPPreferred}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleTPTNToggle();
                    }}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase": {
                        color: isDarkMode ? "var(--neutral-400)" : "#000000",
                        "&.Mui-checked": {
                          color: "var(--primary-700)",
                        },
                        "&.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "var(--primary-700)",
                        },
                      },
                      "& .MuiSwitch-track": {
                        backgroundColor: isDarkMode
                          ? "var(--neutral-600)"
                          : "var(--neutral-300)",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: isTPPreferred ? 600 : 400,
                      fontSize: "0.875rem",
                      fontFamily: "'Lexend', sans-serif",
                      color: isTPPreferred
                        ? "var(--primary-700)"
                        : isDarkMode
                          ? "var(--neutralWhite)"
                          : "#000000",
                      transition: "all 0.2s ease",
                    }}
                  >
                    TP
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontFamily: "'Lexend', sans-serif",
                      fontWeight: 600,
                      color: isTPPreferred
                        ? "var(--primary-700)"
                        : isDarkMode
                          ? "var(--neutralWhite)"
                          : "#000000",
                    }}
                  >
                    {isTPPreferred ? "TP" : "TN"}
                  </Typography>
                </Box>
              )}
            </ListItemButton>
          </Box>

          {/* taascards */}
          <NavItem
            item={{
              text: "cards_library",
              icon: <BackupTableIcon />,
              path: "/taascards",
            }}
            open={open}
            selected={location.pathname === "/taascards"}
          />

          {/* gift cards */}
          <NavItem
            item={{
              text: "gift_cards",
              icon: <GiftIcon />,
              path: "/gift-cards",
            }}
            open={open}
            selected={location.pathname === "/gift-cards"}
          />

          {/* <NavItem
            item={{
              text: "snap_flyers",
              icon: <HistoryEdu />,
              path: "/snap-flyers",
            }}
            open={open}
            selected={location.pathname === "/snap-flyers"}
          /> */}

          <NavItem
            item={{
              text: "ait_card_booking",
              icon: <BookIcon />,
              path: "/talent/ait-card-requests",
            }}
            open={open}
            selected={location.pathname === "/talent/ait-card-requests"}
            onClick={isMobile ? handleClose : undefined}
          />

          {/* flash card */}
          {/* <NavItem
            item={{
              text: "flash_cards",
              icon: <Zap />,
              path: "/flash-cards",
            }}
            open={open}
            selected={location.pathname === "/flash-cards"}
          /> */}

          <Divider
            sx={{
              width: "80%",
              mx: "auto",
              my: 1.5,
              borderColor: isDarkMode
                ? "rgba(148, 163, 184, 0.15)"
                : "rgba(226, 232, 240, 0.8)",
              "&::before, &::after": {
                borderColor: isDarkMode
                  ? "rgba(148, 163, 184, 0.15)"
                  : "rgba(226, 232, 240, 0.8)",
              },
            }}
          />

          {/* <NavItem
            item={{
              text: "draft_cards",
              icon: <Drafts />,
              path: "/draft-cards",
            }}
            open={open}
            selected={location.pathname === "/bookings/stagecard"}
          />

          <NavItem
            item={{
              text: "stage_cards",
              icon: <RecentActors />,
              path: "/stage-cards",
            }}
            open={open}
            selected={location.pathname === "/schedules"}
          /> */}

          {/* <Divider
            sx={{
              width: "80%",
              mx: "auto",
              my: 1.5,
              borderColor: isDarkMode
                ? "rgba(148, 163, 184, 0.15)"
                : "rgba(226, 232, 240, 0.8)",
              "&::before, &::after": {
                borderColor: isDarkMode
                  ? "rgba(148, 163, 184, 0.15)"
                  : "rgba(226, 232, 240, 0.8)",
              },
            }}
          /> */}

          {/* Main Section */}
          <List
            disablePadding
            sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            <NavItem
              item={{
                text: "manage_schedule",
                icon: <TimeIcon />,
                path: "/schedules",
              }}
              open={open}
              selected={location.pathname === "/schedules"}
              onClick={isMobile ? handleClose : undefined}
            />

            <NavItem
              item={{
                text: "STAI",
                icon: <Sparkles />,
                path: "/stai-talent",
              }}
              open={open}
              selected={location.pathname === "/stai-talent"}
              onClick={isMobile ? handleClose : undefined}
            />

            <NavItem
              item={{
                text: "Fan Club",
                icon: <FavoriteRoundedIcon />,
                path: "/fanclub-talent",
              }}
              open={open}
              selected={location.pathname === "/fanclub-talent"}
              onClick={isMobile ? handleClose : undefined}
            />

            <NavItem
              item={{
                text: "instant_bookings",
                icon: <ViewList />,
                path: "/instant-cards/bookings",
              }}
              open={open}
              selected={location.pathname === "/instant-cards/bookings"}
            />

            <NavItem
              item={{
                text: "flash_bookings",
                icon: <ViewList />,
                path: "/flash-bookings",
              }}
              open={open}
              selected={location.pathname === "/flash-bookings"}
            />

            {/* Booking Collapsible Section */}
            <ListItemButton
              onClick={handleBookingToggle}
              sx={{
                borderRadius: 2.5,
                minHeight: 52,
                mx: 1,
                justifyContent: open ? "flex-start" : "center",
                px: open ? 2.5 : 1.5,
                py: 1.4,
                fontFamily: "'Lexend', sans-serif",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                backgroundColor: "transparent",
                color: isDarkMode ? "var(--neutralWhite)" : "#000000",
                border: "1px solid transparent",
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(148, 163, 184, 0.1)"
                    : "rgba(241, 245, 249, 0.8)",
                  color: isDarkMode
                    ? "var(--primary-400)"
                    : "var(--primary-700)",
                  transform: "translateY(-2px)",
                  boxShadow: isDarkMode
                    ? "0 6px 20px rgba(25, 118, 210, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
                    : "0 6px 20px rgba(25, 118, 210, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                  border: isDarkMode
                    ? "1px solid rgba(25, 118, 210, 0.3)"
                    : "1px solid rgba(25, 118, 210, 0.2)",
                  "& .MuiListItemIcon-root": {
                    color: isDarkMode
                      ? "var(--primary-400)"
                      : "var(--primary-700)",
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 1.5 : 0,
                  justifyContent: "center",
                  color: isDarkMode ? "var(--neutralWhite)" : "#000000",
                  transition: "all 0.3s ease",
                  fontSize: "1.3rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <BookIcon />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary={t("bookings")}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontWeight: 500,
                      fontSize: "0.95rem",
                      fontFamily: "'Lexend', sans-serif",
                      color: isDarkMode ? "var(--neutralWhite)" : "#000000",
                    },
                  }}
                />
              )}
              {open && (bookingOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>

            <Collapse in={bookingOpen} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ pl: open ? 4 : 1, gap: 0.5 }}>
                <NavItem
                  item={{
                    text: "suits_bookings",
                    icon: <BookIcon />,
                    path: "/bookings",
                  }}
                  open={open}
                  selected={location.pathname === "/bookings"}
                  onClick={isMobile ? handleClose : undefined}
                />
                <NavItem
                  item={{
                    text: "stagecard_bookings",
                    icon: <BookIcon />,
                    path: "/bookings/stagecard",
                  }}
                  open={open}
                  selected={location.pathname === "/bookings/stagecard"}
                  onClick={isMobile ? handleClose : undefined}
                />

                <NavItem
                  item={{
                    text: "clover_tickets",
                    icon: <BookIcon />,
                    path: "/talent-tickets",
                  }}
                  open={open}
                  selected={location.pathname === "/talent-tickets"}
                  onClick={isMobile ? handleClose : undefined}
                />

                <NavItem
                  item={{
                    text: "business_bookings",
                    icon: <BookIcon />,
                    path: "/bookings/business",
                  }}
                  open={open}
                  selected={location.pathname === "/bookings/business"}
                  onClick={isMobile ? handleClose : undefined}
                />
              </List>
            </Collapse>

            {/* <NavItem
              item={{
                text: "busking",
                icon: <MicIcon />,
                path: "/talent/busking-list",
              }}
              open={open}
              selected={location.pathname === "/talent/busking-list"}
              onClick={isMobile ? handleClose : undefined}
            /> */}
            <NavItem
              item={{
                text: "music",
                icon: <MusicNoteIcon />,
                path: "/music",
              }}
              open={open}
              selected={location.pathname === "/music"}
              onClick={isMobile ? handleClose : undefined}
            />
            <NavItem
              item={{
                text: "avatar",
                icon: <AccountCircleIcon />,
                path: "/talent/avatar",
              }}
              open={open}
              selected={location.pathname === "/talent/live-avatar"}
              onClick={isMobile ? handleClose : undefined}
            />
            <NavItem
              item={{
                text: "avatar_presentation",
                icon: <LiveTvIcon />,
                path: "/tana-mc-presentation/19",
              }}
              open={open}
              selected={location.pathname === "/tana-mc-presentation/17"}
              onClick={isMobile ? handleClose : undefined}
            />
            <NavItem
              item={{
                text: "star_shot",
                icon: <PhotoCameraIcon />,
                path: "/talent/star-shot",
              }}
              open={open}
              selected={location.pathname === "/talent/star-shot"}
              onClick={isMobile ? handleClose : undefined}
            />

            {/* Show Airbank menu items based on registration status */}
            {isAirbankRegistered ? (
              // If registered: Show "Airbank" menu item (routes to status page)
              <NavItem
                item={{
                  text: "Airbank",
                  icon: <VerifiedIcon />,
                  path: AIRBANK_ROUTES.STATUS,
                }}
                open={open}
                selected={isOnStatusPage || isOnManageOrEditPage}
                onClick={handleAirbankClick}
              />
            ) : (
              // If not registered: Show "Register for Airbank" menu item (routes to register page)
              <NavItem
                item={{
                  text: "Register for Airbank",
                  icon: <VerifiedIcon />,
                  path: AIRBANK_ROUTES.REGISTER,
                }}
                open={open}
                selected={isOnRegisterPage}
                onClick={handleAirbankClick}
              />
            )}
            <NavItem
              item={{
                text: "AiT",
                icon: <AccountCircleIcon />,
                path:
                  aitUnreadCount > 0 && talentId
                    ? `/talent/ai-twin/monitor/${talentId}`
                    : "/talent/ai-twins",
              }}
              open={open}
              selected={
                location.pathname === "/talent/ai-twins" ||
                location.pathname.includes("/talent/ai-twin/monitor")
              }
              onClick={() => {
                if (aitUnreadCount > 0 && talentId) {
                  setAitUnreadCount(0); // Reset count when navigating
                }
                if (isMobile) {
                  handleClose?.();
                }
              }}
              badgeContent={aitUnreadCount > 0 ? aitUnreadCount : undefined}
            />
            <NavItem
              item={{
                text: "premium_content",
                icon: <PremiumContentIcon />,
                path: "/premium-content",
              }}
              open={open}
              selected={location.pathname === "/premium-content"}
              onClick={isMobile ? handleClose : undefined}
            />

            <NavItem
              item={{
                text: "gtp",
                icon: <KanbanIcon />,
                path: "/gtp",
              }}
              open={open}
              selected={location.pathname === "/gtp"}
              onClick={isMobile ? handleClose : undefined}
            />

            <NavItem
              item={{
                text: "business",
                icon: <KanbanIcon />,
                path: "/business",
              }}
              open={open}
              selected={location.pathname === "/business"}
            />

            <Divider
              sx={{
                width: "80%",
                my: 1.5,
                mx: "auto",
                borderColor: isDarkMode
                  ? "rgba(148, 163, 184, 0.15)"
                  : "rgba(226, 232, 240, 0.8)",
                "&::before, &::after": {
                  borderColor: isDarkMode
                    ? "rgba(148, 163, 184, 0.15)"
                    : "rgba(226, 232, 240, 0.8)",
                },
              }}
            />

            <ListItemButton
              onClick={handleFanCardToggle}
              sx={{
                borderRadius: 2.5,
                minHeight: 52,
                mx: 1,
                justifyContent: open ? "flex-start" : "center",
                px: open ? 2.5 : 1.5,
                py: 1.4,
                fontFamily: "'Lexend', sans-serif",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                backgroundColor: "transparent",
                color: isDarkMode ? "var(--neutralWhite)" : "#000000",
                border: "1px solid transparent",
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(148, 163, 184, 0.1)"
                    : "rgba(241, 245, 249, 0.8)",
                  color: isDarkMode
                    ? "var(--primary-400)"
                    : "var(--primary-700)",
                  transform: "translateY(-2px)",
                  boxShadow: isDarkMode
                    ? "0 6px 20px rgba(25, 118, 210, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
                    : "0 6px 20px rgba(25, 118, 210, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                  border: isDarkMode
                    ? "1px solid rgba(25, 118, 210, 0.3)"
                    : "1px solid rgba(25, 118, 210, 0.2)",
                  "& .MuiListItemIcon-root": {
                    color: isDarkMode
                      ? "var(--primary-400)"
                      : "var(--primary-700)",
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 1.5 : 0,
                  justifyContent: "center",
                  color: isDarkMode ? "var(--neutralWhite)" : "#000000",
                  transition: "all 0.3s ease",
                  fontSize: "1.3rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <BookIcon />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary={t("fan_cards")}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontWeight: 500,
                      fontSize: "0.95rem",
                      fontFamily: "'Lexend', sans-serif",
                      color: isDarkMode ? "var(--neutralWhite)" : "#000000",
                    },
                  }}
                />
              )}
              {open && (fanCardOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>

            <Collapse in={fanCardOpen} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ pl: open ? 4 : 1, gap: 0.5 }}>
                <NavItem
                  item={{
                    text: "flips",
                    icon: <VideoLibrary />,
                    path: "/flips",
                  }}
                  open={open}
                  selected={location.pathname === "/flips"}
                  onClick={isMobile ? handleClose : undefined}
                />
                <NavItem
                  item={{
                    text: "media_gallery",
                    icon: <Collections />,
                    path: "/media-gallery",
                  }}
                  open={open}
                  selected={location.pathname === "/media-gallery"}
                  onClick={isMobile ? handleClose : undefined}
                />

                <NavItem
                  item={{
                    text: "board_posts",
                    icon: <PostAdd />,
                    path: "/posts",
                  }}
                  open={open}
                  selected={location.pathname === "/posts"}
                  onClick={isMobile ? handleClose : undefined}
                />
              </List>
            </Collapse>

            <Divider
              sx={{
                width: "80%",
                mx: "auto",
                borderColor: isDarkMode
                  ? "rgba(148, 163, 184, 0.15)"
                  : "rgba(226, 232, 240, 0.8)",
                "&::before, &::after": {
                  borderColor: isDarkMode
                    ? "rgba(148, 163, 184, 0.15)"
                    : "rgba(226, 232, 240, 0.8)",
                },
              }}
            />

            <NavItem
              item={{
                text: "dashboard",
                icon: <KanbanIcon />,
                path: "/talent-dashboard",
              }}
              open={open}
              selected={location.pathname === "/talent-dashboard"}
              onClick={isMobile ? handleClose : undefined}
            />

            <NavItem
              item={{
                text: "message",
                icon: <ChatBubble />,
                path: "/messages",
              }}
              open={open}
              selected={location.pathname === "/messages"}
              onClick={isMobile ? handleClose : undefined}
              badgeContent={unreadMessageCounter}
            />
            <NavItem
              item={{
                text: "coffee_calls",
                icon: <CoffeeIcon />,
                path: "/coffee",
              }}
              open={open}
              selected={location.pathname === "/coffee"}
              onClick={isMobile ? handleClose : undefined}
            />
            <NavItem
              item={{ text: "wallet", icon: <Wallet />, path: "/wallet" }}
              open={open}
              selected={location.pathname === "/wallet"}
              onClick={isMobile ? handleClose : undefined}
            />
            <NavItem
              item={{
                text: "aibooth_images",
                icon: <PhotoCameraIcon />,
                path: "talent/aibooth-images",
              }}
              open={open}
              selected={location.pathname === "talent/aibooth-images"}
              onClick={isMobile ? handleClose : undefined}
            />
            <NavItem
              item={{
                text: "golden_card",
                icon: <ImageIcon />,
                path: "talent/golden-card",
              }}
              open={open}
              selected={location.pathname === "talent/golden-card"}
              onClick={isMobile ? handleClose : undefined}
            />
          </List>
        </Box>
      </Box>

      {/* Bottom fixed section */}
      <Box sx={{ mb: 2 }}>
        <List
          disablePadding
          sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          {[
            { text: "settings", icon: <SettingsIcon />, path: "/settings" },
            { text: "help", icon: <HelpIcon />, path: "/help" },
          ].map((item) => (
            <NavItem
              key={item.text}
              item={item}
              open={open}
              selected={location.pathname === item.path}
              onClick={isMobile ? handleClose : undefined}
            />
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default TalentSidebar;
